import {
  PRIVATE_SESSION_IDLE_MS,
  requirePrivateSessionIdleMs,
  type PrivateSessionIdleMs,
} from "./sessionPolicy";

const validTime = (value: number) => Number.isSafeInteger(value) && value >= 0;

/** Synchronous application idle policy, not on-chain key revocation.
 * Only the caller can determine whether activity represents a trusted user action. */
export class PrivateSessionIdle {
  private activityAt?: number;
  private observedAt?: number;
  private deadlineAt?: number;
  private duration: PrivateSessionIdleMs;

  constructor(idleTimeoutMs: number = PRIVATE_SESSION_IDLE_MS) {
    this.duration = requirePrivateSessionIdleMs(idleTimeoutMs);
  }

  get idleTimeoutMs(): PrivateSessionIdleMs {
    return this.duration;
  }

  /** A policy change never revives an expired session. Shorter deadlines apply
   * immediately; longer ones require the next deliberate activity. */
  setIdleTimeoutMs(idleTimeoutMs: number, now = Date.now()): boolean {
    const next = requirePrivateSessionIdleMs(idleTimeoutMs);
    const active = this.check(now);
    this.duration = next;
    if (!active) return false;
    this.deadlineAt = Math.min(this.deadlineAt!, this.activityAt! + next);
    return this.check(now);
  }

  get lastActivity(): number | undefined {
    return this.activityAt;
  }
  get expiresAt(): number | undefined {
    return this.deadlineAt;
  }

  start(
    lastActivity: number,
    now = Date.now(),
    savedExpiresAt?: number
  ): boolean {
    this.clear();
    const deadline = Math.min(
      lastActivity + this.duration,
      savedExpiresAt ?? lastActivity + this.duration
    );
    if (
      !validTime(lastActivity) ||
      !validTime(now) ||
      (savedExpiresAt !== undefined && !validTime(savedExpiresAt)) ||
      !Number.isSafeInteger(lastActivity + this.duration) ||
      !validTime(deadline) ||
      now < lastActivity ||
      now >= deadline
    )
      return false;
    this.activityAt = lastActivity;
    this.observedAt = now;
    this.deadlineAt = deadline;
    return true;
  }

  check(now = Date.now()): boolean {
    if (
      this.activityAt === undefined ||
      this.observedAt === undefined ||
      this.deadlineAt === undefined ||
      !validTime(now) ||
      now < this.observedAt ||
      now >= this.deadlineAt
    ) {
      this.clear();
      return false;
    }
    this.observedAt = now;
    return true;
  }

  activity(now = Date.now()): boolean {
    if (!this.check(now) || !Number.isSafeInteger(now + this.duration)) {
      this.clear();
      return false;
    }
    this.activityAt = now;
    this.deadlineAt = now + this.duration;
    return true;
  }

  clear(): void {
    this.activityAt = undefined;
    this.observedAt = undefined;
    this.deadlineAt = undefined;
  }
}
